import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useState, type ReactNode } from 'react';
import { useAuth } from '../lib/auth.js';
import { useTheme } from '../lib/theme.js';
import { apiLogout } from '../lib/api.js';
import { Logo } from './Logo.js';
import { CommandPalette } from './CommandPalette.js';
import { NotificationsBell } from './NotificationsBell.js';
import { Button, useToast } from './ui/index.js';
import { ROLE_LABELS } from '@hospasense/contracts';

interface NavItem {
  to: string;
  label: string;
  /** Null = visible to every authenticated operator (e.g. the role home). */
  permission: string | null;
  icon: ReactNode;
}

/**
 * Application chrome: navy sidebar (navigation reflects the operator's
 * resolved API permissions), header with theme toggle, ⌘K palette, sign-out.
 */
export function AppShell() {
  const { me, hasPermission } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const toast = useToast();
  const location = useLocation();
  const [navOpen, setNavOpen] = useState(false);

  // Close the drawer on navigation, so tapping a destination does not leave
  // the overlay covering the page it just opened.
  useEffect(() => setNavOpen(false), [location.pathname]);

  // Escape closes it, matching the palette and every other overlay here.
  useEffect(() => {
    if (!navOpen) return undefined;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setNavOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navOpen]);

  const nav: NavItem[] = [
    { to: '/home', label: 'My Home', permission: null, icon: <DotIcon /> },
    { to: '/', label: 'Mission Overview', permission: 'overview.read', icon: <DotIcon /> },
    {
      // Above Organisations on purpose: inbound work comes before the estate it
      // feeds. A hospital waiting for a decision is the most perishable thing
      // on this console.
      to: '/signups',
      label: 'Signups',
      permission: 'signup.read',
      icon: <DotIcon />,
    },
    {
      // Next to Organisations because it is the commonest follow-up to one:
      // a hospital emails asking for a module and somebody has to act on it.
      to: '/modules',
      label: 'Module access',
      permission: 'entitlement.read',
      icon: <DotIcon />,
    },
    {
      to: '/organisations',
      label: 'Organisations',
      permission: 'organisation.read',
      icon: <DotIcon />,
    },
    { to: '/onboarding', label: 'Onboarding', permission: 'onboarding.read', icon: <DotIcon /> },
    { to: '/commercial', label: 'Commercial', permission: 'contract.read', icon: <DotIcon /> },
    {
      to: '/entitlements',
      label: 'Entitlements',
      permission: 'entitlement.read',
      icon: <DotIcon />,
    },
    { to: '/billing', label: 'B2B Billing', permission: 'billing.read', icon: <DotIcon /> },
    {
      to: '/provisioning',
      label: 'Provisioning',
      permission: 'provisioning.read',
      icon: <DotIcon />,
    },
    { to: '/fleet', label: 'Fleet Topology', permission: 'fleet.read', icon: <DotIcon /> },
    { to: '/releases', label: 'Releases & Rollout', permission: 'release.read', icon: <DotIcon /> },
    {
      to: '/reliability',
      label: 'SRE & Reliability',
      permission: 'reliability.read',
      icon: <DotIcon />,
    },
    { to: '/support', label: 'Support', permission: 'case.read', icon: <DotIcon /> },
    { to: '/approvals', label: 'Approvals', permission: 'approval.read', icon: <DotIcon /> },
    { to: '/analytics', label: 'Analytics', permission: 'analytics.read', icon: <DotIcon /> },
    { to: '/health', label: 'Customer Health', permission: 'health.read', icon: <DotIcon /> },
    { to: '/compliance', label: 'Compliance', permission: 'compliance.read', icon: <DotIcon /> },
    { to: '/risk', label: 'Risk & Privacy', permission: 'risk.read', icon: <DotIcon /> },
    { to: '/automation', label: 'Automation', permission: 'automation.read', icon: <DotIcon /> },
    { to: '/reports', label: 'Reports', permission: 'report.read', icon: <DotIcon /> },
    { to: '/audit', label: 'Audit log', permission: 'audit.read', icon: <DotIcon /> },
  ].filter((item) => item.permission === null || hasPermission(item.permission));

  const signOut = async (): Promise<void> => {
    try {
      await apiLogout();
    } catch {
      // Session may already be gone; either way we return to the login page.
    }
    toast.success('Signed out');
    navigate('/login');
    window.location.reload();
  };

  return (
    <div className="flex min-h-screen bg-background">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:bg-surface focus:px-3 focus:py-2 focus:text-sm"
      >
        Skip to main content
      </a>
      <aside className="hidden w-60 shrink-0 flex-col bg-sidebar text-sidebar-fg md:flex">
        <div className="border-b border-sidebar-border px-4 py-5">
          {/* Full lockup: the brand name is displayed, so the open mark + wordmark form is used. */}
          <Logo variant="full" tone="on-dark" height={26} title="HospaSense Control" />
          <p className="mt-1 text-[11px] uppercase tracking-widest text-sidebar-fg/75">Control</p>
        </div>
        <nav aria-label="Primary" className="flex-1 space-y-1 p-3">
          {nav.map((item) => (
            <NavItemLink key={item.to} item={item} />
          ))}
        </nav>
        {me && (
          <div className="border-t border-sidebar-border p-4 text-xs">
            <p className="truncate font-medium">{me.displayName}</p>
            <p className="mt-0.5 truncate text-sidebar-fg/75">
              {me.roles.map((r) => ROLE_LABELS[r] ?? r).join(', ')}
            </p>
          </div>
        )}
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex items-center justify-between gap-3 border-b border-border bg-surface px-4 py-2 md:px-6">
          <div className="flex items-center gap-2 md:hidden">
            <Button
              variant="ghost"
              size="icon"
              aria-label="Open navigation"
              aria-expanded={navOpen}
              aria-controls="mobile-nav"
              data-testid="mobile-nav-toggle"
              onClick={() => setNavOpen(true)}
            >
              <MenuIcon />
            </Button>
            {/* Narrow header: name not written, so the solo trace mark is correct. */}
            <Logo variant="mark" tone={theme === 'dark' ? 'on-dark' : 'on-light'} height={28} />
          </div>
          <div className="hidden items-center gap-2 text-xs text-text-muted md:flex">
            <kbd className="rounded-sm border border-border bg-bg-subtle px-1.5 py-0.5 font-mono">
              Ctrl K
            </kbd>
            <span>command palette</span>
          </div>
          <div className="flex items-center gap-2">
            <NotificationsBell />
            <Button
              variant="ghost"
              size="icon"
              aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
              aria-pressed={theme === 'dark'}
              onClick={toggle}
              data-testid="theme-toggle"
            >
              {theme === 'dark' ? <SunIcon /> : <MoonIcon />}
            </Button>
            <Button variant="outline" size="sm" onClick={() => void signOut()}>
              Sign out
            </Button>
          </div>
        </header>
        <main id="main-content" className="min-w-0 flex-1 p-4 md:p-6">
          <Outlet />
        </main>

        {/* Narrow-viewport navigation. Built from the SAME `nav` array as the
            sidebar, so it reflects the operator's resolved permissions and
            cannot drift from it. Without this a phone user lands on their home
            page and can reach nothing else. */}
        {navOpen && (
          <div className="fixed inset-0 z-40 md:hidden">
            <button
              type="button"
              aria-label="Close navigation"
              className="absolute inset-0 bg-black/50"
              onClick={() => setNavOpen(false)}
            />
            <div
              id="mobile-nav"
              role="dialog"
              aria-modal="true"
              aria-label="Navigation"
              className="absolute inset-y-0 left-0 flex w-72 max-w-[85vw] flex-col overflow-y-auto bg-sidebar text-sidebar-fg shadow-xl"
            >
              <div className="flex items-center justify-between border-b border-sidebar-border px-4 py-4">
                <Logo variant="full" tone="on-dark" height={24} title="HospaSense Control" />
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label="Close navigation"
                  onClick={() => setNavOpen(false)}
                >
                  <CloseIcon />
                </Button>
              </div>
              <nav aria-label="Primary (narrow)" className="flex-1 space-y-1 p-3">
                {nav.map((item) => (
                  <NavItemLink key={item.to} item={item} />
                ))}
              </nav>
              {me && (
                <div className="border-t border-sidebar-border p-4 text-xs">
                  <p className="truncate font-medium">{me.displayName}</p>
                  <p className="mt-0.5 truncate text-sidebar-fg/75">
                    {me.roles.map((r) => ROLE_LABELS[r] ?? r).join(', ')}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      <CommandPalette />
    </div>
  );
}

function NavItemLink({ item }: { item: NavItem }) {
  return (
    <NavLink
      to={item.to}
      end={item.to === '/'}
      className={({ isActive }) =>
        [
          'flex items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors duration-150',
          'focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none',
          isActive
            ? 'bg-sidebar-active text-sidebar-active-fg font-medium'
            : 'hover:bg-sidebar-hover',
        ].join(' ')
      }
    >
      {item.icon}
      {item.label}
    </NavLink>
  );
}

function MenuIcon() {
  return (
    <svg
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      className="size-5"
      aria-hidden="true"
    >
      <path strokeWidth="1.75" strokeLinecap="round" d="M3 5h14M3 10h14M3 15h14" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      className="size-5"
      aria-hidden="true"
    >
      <path strokeWidth="1.75" strokeLinecap="round" d="M5 5l10 10M15 5L5 15" />
    </svg>
  );
}

function DotIcon() {
  return <span className="size-1.5 rounded-full bg-current opacity-70" aria-hidden="true" />;
}

function MoonIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" className="size-4" aria-hidden="true">
      <path d="M17 11.5A7.5 7.5 0 018.5 3a7.5 7.5 0 108.5 8.5z" />
    </svg>
  );
}

function SunIcon() {
  return (
    <svg
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      className="size-4"
      aria-hidden="true"
    >
      <circle cx="10" cy="10" r="3.5" strokeWidth="1.6" />
      <path
        strokeWidth="1.6"
        strokeLinecap="round"
        d="M10 2v2M10 16v2M2 10h2M16 10h2M4.5 4.5l1.4 1.4M14.1 14.1l1.4 1.4M15.5 4.5l-1.4 1.4M5.9 14.1l-1.4 1.4"
      />
    </svg>
  );
}
